import type { ScanGroupOptimized, ScanPhoto, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import { isOtherTask } from '@kaiizen/kai-scan-template-system';

import { getGroupTitle, getPartPhotoEntries, getTaskTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatDate } from '@/Client/Presenters';

import type { FocusedPhotoStripEntry } from './focusedStrip/focusedStripTypes';
import {
  ADD_MEDIA_LABEL,
  GROUP_PHOTO_CHIP_LABEL,
  NON_STANDARD_TASK_CHIP_LABEL_PREFIX,
  TASK_PHOTO_CHIP_LABEL_FALLBACK,
} from './focusedStrip/focusedStripTypes';

/** Milestone walks get inline labels; progress walks stay date-only (mockup rule). */
export const getFocusedPhotoWalkLabel = (photo: ScanPhoto): string | undefined => {
  const tag = photo.tags?.find(Boolean);
  return tag && !/^progress/i.test(tag) ? tag[0]!.toUpperCase() + tag.slice(1) : undefined;
};

export const toFocusedPhotoStripEntry = (
  photo: ScanPhoto,
  scope: FocusedPhotoStripEntry['scope'],
  context: { groupId?: string; groupLabel?: string; taskId?: string; taskLabel?: string } = {},
): FocusedPhotoStripEntry => ({
  photoId: photo.photoId,
  scope,
  addedAt: photo.addedAt,
  addedByUserId: photo.addedByUserId,
  photoSource: photo.photoSource,
  dateLabel: photo.addedAt ? formatDate(photo.addedAt) : undefined,
  walkLabel: getFocusedPhotoWalkLabel(photo),
  ...context,
});

const sortFocusedPhotoStripEntriesNewestFirst = (
  entries: readonly FocusedPhotoStripEntry[],
): FocusedPhotoStripEntry[] =>
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => {
      const leftTime = typeof left.entry.addedAt === 'number'
        ? left.entry.addedAt
        : left.entry.addedAt
        ? Date.parse(left.entry.addedAt)
        : Number.NaN;
      const rightTime = typeof right.entry.addedAt === 'number'
        ? right.entry.addedAt
        : right.entry.addedAt
        ? Date.parse(right.entry.addedAt)
        : Number.NaN;
      const leftHasTime = Number.isFinite(leftTime);
      const rightHasTime = Number.isFinite(rightTime);

      if (leftHasTime && rightHasTime && leftTime !== rightTime) return rightTime - leftTime;
      if (leftHasTime !== rightHasTime) return leftHasTime ? -1 : 1;
      return left.index - right.index;
    })
    .map(({ entry }) => entry);

const appendUniquePhotoEntries = (
  target: FocusedPhotoStripEntry[],
  entryIndexByPhotoId: Map<string, number>,
  photos: readonly ScanPhoto[],
  scope: FocusedPhotoStripEntry['scope'],
  context: { groupId?: string; groupLabel?: string; taskId?: string; taskLabel?: string },
) => {
  for (const photo of photos) {
    if (!photo.photoId) continue;
    const existingIndex = entryIndexByPhotoId.get(photo.photoId);
    if (existingIndex != null) {
      if (scope === 'task' && target[existingIndex]?.scope === 'group') {
        target[existingIndex] = toFocusedPhotoStripEntry(photo, scope, context);
      }
      continue;
    }
    entryIndexByPhotoId.set(photo.photoId, target.length);
    target.push(toFocusedPhotoStripEntry(photo, scope, context));
  }
};

const appendGroupPhotoEntries = (
  target: FocusedPhotoStripEntry[],
  entryIndexByPhotoId: Map<string, number>,
  group: ScanGroupOptimized,
) => {
  // Titled the way the rest of the editor titles a group, so a group the user
  // renamed is tagged with the name they gave it rather than its template's.
  const groupLabel = getGroupTitle(group, GROUP_PHOTO_CHIP_LABEL);
  appendUniquePhotoEntries(
    target,
    entryIndexByPhotoId,
    getPartPhotoEntries(group),
    'group',
    { groupId: group.id, groupLabel },
  );
};

const appendTaskPhotoEntries = (
  target: FocusedPhotoStripEntry[],
  entryIndexByPhotoId: Map<string, number>,
  group: ScanGroupOptimized,
  task: ScanTaskOptimized,
) => {
  const taskLabel = task.template && isOtherTask(task) && task.customLabel
    ? `${NON_STANDARD_TASK_CHIP_LABEL_PREFIX}${task.customLabel}`
    : getTaskTitle(task, TASK_PHOTO_CHIP_LABEL_FALLBACK);
  appendUniquePhotoEntries(
    target,
    entryIndexByPhotoId,
    getPartPhotoEntries(task),
    'task',
    { groupId: group.id, taskId: task.id, taskLabel },
  );
};

export type BuildFocusedPhotoStripEntriesParams =
  | { mode: 'task'; group: ScanGroupOptimized; task: ScanTaskOptimized }
  | { mode: 'group'; group: ScanGroupOptimized }
  | { mode: 'scope'; groups: readonly ScanGroupOptimized[] };

/** Builds ordered, deduped strip entries for task, group, or all-scope edit routes. */
export const buildFocusedPhotoStripEntries = (
  params: BuildFocusedPhotoStripEntriesParams,
): FocusedPhotoStripEntry[] => {
  const entries: FocusedPhotoStripEntry[] = [];
  const entryIndexByPhotoId = new Map<string, number>();

  if (params.mode === 'task') {
    appendTaskPhotoEntries(entries, entryIndexByPhotoId, params.group, params.task);
    return sortFocusedPhotoStripEntriesNewestFirst(entries);
  }

  if (params.mode === 'group') {
    appendGroupPhotoEntries(entries, entryIndexByPhotoId, params.group);
    for (const task of params.group.tasks ?? []) {
      appendTaskPhotoEntries(entries, entryIndexByPhotoId, params.group, task);
    }
    return sortFocusedPhotoStripEntriesNewestFirst(entries);
  }

  for (const group of params.groups) {
    appendGroupPhotoEntries(entries, entryIndexByPhotoId, group);
    for (const task of group.tasks ?? []) {
      appendTaskPhotoEntries(entries, entryIndexByPhotoId, group, task);
    }
  }
  return sortFocusedPhotoStripEntriesNewestFirst(entries);
};

export type FocusedPhotoStripEditorMode = BuildFocusedPhotoStripEntriesParams['mode'];

export const getFocusedPhotoStripEditorConfig = ({
  group,
  groups,
  task,
  taskId,
}: {
  group?: ScanGroupOptimized;
  groups: readonly ScanGroupOptimized[];
  task?: ScanTaskOptimized;
  taskId?: string;
}): {
  entries: FocusedPhotoStripEntry[];
  title: string;
  emptyLabel: string;
  /** The strip's add action uploads — to the task, the group, or (at scope level) the scan root.
   *  Rendered as an icon button, so this is its accessible name rather than visible text. */
  actionLabel?: string;
  galleryScope: { groupId?: string; taskId?: string };
  mode: FocusedPhotoStripEditorMode;
} => {
  if (taskId && group && task) {
    return {
      mode: 'task',
      entries: buildFocusedPhotoStripEntries({ mode: 'task', group, task }),
      title: 'Task Photos',
      emptyLabel: 'No photos yet for this task',
      actionLabel: ADD_MEDIA_LABEL,
      galleryScope: { groupId: group.id, taskId: task.id },
    };
  }

  if (group) {
    return {
      mode: 'group',
      entries: buildFocusedPhotoStripEntries({ mode: 'group', group }),
      title: 'Group and Task Photos',
      emptyLabel: 'No photos yet for this group',
      actionLabel: ADD_MEDIA_LABEL,
      galleryScope: { groupId: group.id },
    };
  }

  return {
    mode: 'scope',
    entries: buildFocusedPhotoStripEntries({ mode: 'scope', groups }),
    title: 'Group and Task Photos',
    emptyLabel: 'No photos yet for this scope',
    // Scope level attaches to the scan root itself — the "Curbside Photo" bucket
    // (see galleryPhotoOperations.ts's empty-context target) — same as a group or
    // task strip's "+ Add".
    actionLabel: ADD_MEDIA_LABEL,
    galleryScope: {},
  };
};
